/**
 * ShareModal — matches GlobalFeed / SearchModal shell (rounded-[40px], gray-900 card).
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Share2,
  Download,
  Check,
  RectangleVertical,
  Link2,
  Share
} from 'lucide-react';
import { motion } from 'framer-motion';
import { haptics } from '../utils/haptics';
import { useModalA11y } from '../hooks/useModalA11y';

export default function ShareModal({
  isOpen,
  onClose,
  pollData,
  activeFilter = 'main'
}) {
  const [intent, setIntent] = useState('invite');
  const [isCopied, setIsCopied] = useState(false);
  const [canShare, setCanShare] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    setCanShare(typeof navigator !== 'undefined' && typeof navigator.share === 'function');
  }, []);

  const hasVoted = useMemo(() => {
    return !!(
      pollData?.voted || 
      pollData?.hasVoted || 
      pollData?.user_voted || 
      pollData?.hasParticipated ||
      pollData?.isMyPoll ||
      activeFilter === 'result'
    );
  }, [pollData, activeFilter]);

  useEffect(() => {
    if (activeFilter && activeFilter !== 'main') {
      setIntent(hasVoted ? 'result' : 'invite');
    } else {
      setIntent('invite');
    }
  }, [activeFilter, isOpen, hasVoted]);

  const shareUrl = useMemo(() => {
    if (typeof window === 'undefined' || !pollData?.id) return '';
    const params = new URLSearchParams({ intent });
    return `${window.location.origin}/poll/${pollData.id}?${params.toString()}`;
  }, [pollData?.id, intent]);

  const mainBars = useMemo(() => {
    if (pollData?.mainResults?.length) return pollData.mainResults;
    const opts = pollData?.options || [];
    const totalWeight = pollData?.total_weight || 1;
    return opts.slice(0, 4).map((opt, idx) => {
      const weight = pollData[`w${idx}`] || 0;
      const percent = totalWeight > 0 ? Math.round((weight / totalWeight) * 100) : 0;
      return { label: opt, percent };
    });
  }, [pollData]);

  const handleCopyLink = useCallback(() => {
    if (!shareUrl) return;
    haptics.medium();
    navigator.clipboard.writeText(shareUrl).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2200);
    });
  }, [shareUrl]);

  const handleNativeShare = async () => {
    haptics.medium();
    setIsSharing(true);
    try {
      const imageUrl = `/api/share/poll/${pollData.id}/image.png?format=card&intent=${intent}`;
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const file = new File([blob], `opinion-poll-${pollData.id}.png`, { type: 'image/png' });
      const shareData = {
        title: pollData.question,
        text: intent === 'invite' ? `Vote: ${pollData.question}` : `Results: ${pollData.question}`,
        url: shareUrl
      };
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ ...shareData, files: [file] });
      } else {
        await navigator.share(shareData);
      }
    } catch (err) {
      console.warn('Share failed:', err);
      try { await navigator.share({ title: pollData.question, url: shareUrl }); } catch (e) {
        if (e.name !== 'AbortError') handleCopyLink();
      }
    } finally { setIsSharing(false); }
  };

  const handleDownload = async () => {
    haptics.heavy();
    setIsGenerating(true);
    try {
      const imageUrl = `/api/share/poll/${pollData.id}/image.png?format=card&intent=${intent}`;
      const link = document.createElement('a');
      link.href = imageUrl;
      link.download = `opinion-${pollData.id}-${intent}.png`;
      link.click();
    } catch (err) { console.error('Download failed:', err); }
    finally { setIsGenerating(false); }
  };

  const containerRef = useModalA11y(isOpen, onClose);

  if (!isOpen || !pollData) return null;

  return (
    <div className="fixed inset-0 z-[500] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" onClick={onClose} aria-hidden />
      <motion.div
        ref={containerRef}
        role="dialog"
        aria-modal="true"
        aria-label="Share Studio"
        initial={{ y: '100%', opacity: 0 }} animate={{ y: 0, opacity: 1 }}
        transition={{ type: 'spring', stiffness: 320, damping: 34 }}
        className="relative w-full max-w-4xl bg-white dark:bg-gray-900 rounded-t-panel sm:rounded-modal shadow-2xl border border-gray-100 dark:border-gray-800 flex flex-col max-h-[92vh] overflow-hidden"
      >
        <div className="w-10 h-1 bg-gray-200 dark:bg-gray-700 rounded-full mx-auto mt-2.5 sm:hidden shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-primary-600/10 border border-primary-600/20"><Share className="text-primary-600 w-4 h-4" /></div>
            <div>
              <h2 className="text-xl font-black text-gray-900 dark:text-white tracking-tight leading-none">Share Studio</h2>
              <p className="text-2xs font-bold text-gray-500 uppercase tracking-widest mt-1">Share your poll results</p>
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close share studio" className="p-2 bg-gray-50 dark:bg-gray-800 rounded-full text-gray-500 hover:text-gray-600 transition-colors"><X size={20} /></button>
        </div>

        {/* 2-Column Body */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                   {/* LEFT: PREVIEW */}
          <div className="w-full md:w-[460px] p-6 bg-gray-50 dark:bg-black/20 border-r border-gray-100 dark:border-gray-800 flex flex-col items-center justify-center relative min-h-[400px]">
            <div className="w-full max-w-[380px] aspect-square rounded-2xl border border-gray-200 dark:border-gray-700/80 bg-[#0B0F1A] shadow-2xl overflow-hidden relative group">
              {/* Actual Backend Image (1:1 Parity) */}
              <motion.img 
                key={`${intent}-${pollData.id}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                src={`/api/share/poll/${pollData.id}/image.png?intent=${intent}&format=card&v=3`}
                alt="Poll Preview"
                className="w-full h-full object-contain"
                onLoad={() => setIsGenerating(false)}
              />
              
              {/* Optional: Overlay indicating it's a live preview */}
              <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                <span className="text-2xs font-black text-white/80 uppercase tracking-widest">Live Export Preview</span>
              </div>
            </div>
            <div className="mt-6 flex flex-col items-center gap-2">
              <p className="text-2xs font-bold text-gray-500 text-center uppercase tracking-widest">High Quality Image</p>
              <div className="flex items-center gap-1.5 px-3 py-1 bg-violet-500/10 border border-violet-500/20 rounded-full">
                <Check size={10} className="text-violet-500" />
                <span className="text-2xs font-black text-violet-500 uppercase tracking-tight">1:1 Visual Parity</span>
              </div>
            </div>
          </div>

          {/* RIGHT: CONTROLS (REDESIGNED) */}
          <div className="flex-1 p-8 sm:p-10 overflow-y-auto no-scrollbar flex flex-col bg-white dark:bg-gray-900">
            <div className="max-w-[400px] mx-auto w-full space-y-10">
              
              {/* 1. SELECT CONTENT TYPE (Segmented Control) */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-[11px] font-black uppercase tracking-[0.25em] text-gray-500 dark:text-gray-500">1. Share Content</span>
                  {!hasVoted && (
                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-400/10 border border-amber-400/20">
                      <RectangleVertical size={10} className="text-amber-500" />
                      <span className="text-2xs font-black uppercase text-amber-500 tracking-tight">Voter Locked</span>
                    </div>
                  )}
                </div>
                
                <div className="relative p-1 bg-gray-100 dark:bg-gray-800/50 rounded-2xl flex items-center">
                  <div 
                    className={`absolute inset-y-1 w-[calc(50%-4px)] bg-white dark:bg-gray-700 shadow-xl rounded-xl transition-all duration-300 ease-out z-0 ${intent === 'result' ? 'translate-x-[calc(100%+4px)]' : 'translate-x-0'}`}
                  />
                  <button 
                    type="button" onClick={() => { setIntent('invite'); haptics.impact(); }}
                    className={`relative z-10 flex-1 py-3 text-[11px] font-black uppercase tracking-widest transition-colors ${intent === 'invite' ? 'text-gray-900 dark:text-white' : 'text-gray-500 hover:text-gray-500'}`}
                  >
                    Invite
                  </button>
                  <button
                    type="button" disabled={!hasVoted}
                    title={!hasVoted ? 'Vote on this poll first to share its results' : undefined}
                    aria-label={!hasVoted ? 'Results — locked until you vote on this poll' : 'Share results'}
                    onClick={() => { if (!hasVoted) return; setIntent('result'); haptics.impact(); }}
                    className={`relative z-10 flex-1 py-3 text-[11px] font-black uppercase tracking-widest transition-colors ${intent === 'result' ? 'text-gray-900 dark:text-white' : (hasVoted ? 'text-gray-500 hover:text-gray-500' : 'opacity-30 cursor-not-allowed')}`}
                  >
                    Results
                  </button>
                </div>
              </div>

              {/* 2. CHOOSE CHANNEL */}
              <div className="space-y-4">
                <span className="text-[11px] font-black uppercase tracking-[0.25em] text-gray-500 dark:text-gray-500">2. Post to Socials</span>
                
                <div className="space-y-3">
                  {/* Primary Native Share */}
                  <button 
                    type="button" onClick={handleNativeShare} disabled={isSharing}
                    className="group relative w-full p-0.5 rounded-2xl bg-gradient-to-br from-primary-600 via-[#8B5CF6] to-secondary-600 shadow-xl shadow-violet-500/20 active:scale-95 transition-all overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="relative py-4 rounded-[14px] flex items-center justify-center gap-3">
                      {isSharing ? (
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      ) : (
                        <Share2 size={18} className="text-white" />
                      )}
                      <span className="text-white font-black text-[13px] uppercase tracking-[0.15em]">
                        {isSharing ? 'Rendering...' : 'Share Everywhere'}
                      </span>
                    </div>
                  </button>

                  <div className="grid grid-cols-2 gap-3">
                    {/* Copy Link */}
                    <button 
                      type="button" onClick={handleCopyLink}
                      className={`flex flex-col items-center gap-2 py-4 rounded-2xl border transition-all active:scale-95 ${
                        isCopied 
                        ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-600' 
                        : 'bg-gray-50 dark:bg-gray-800/40 border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-200 dark:hover:border-gray-700 hover:bg-white dark:hover:bg-gray-800'
                      }`}
                    >
                      {isCopied ? <Check size={18} className="text-emerald-500" /> : <Link2 size={18} />}
                      <span className="text-2xs font-black uppercase tracking-widest">{isCopied ? 'Copied' : 'Link'}</span>
                    </button>

                    {/* Download */}
                    <button 
                      type="button" onClick={handleDownload} disabled={isGenerating}
                      className="flex flex-col items-center gap-2 py-4 rounded-2xl bg-gray-50 dark:bg-gray-800/40 border border-gray-100 dark:border-gray-800 text-gray-600 dark:text-gray-400 hover:border-gray-200 dark:hover:border-gray-700 hover:bg-white dark:hover:bg-gray-800 transition-all active:scale-95 disabled:opacity-50"
                    >
                      {isGenerating ? <div className="w-[18px] h-[18px] border-2 border-primary-600 rounded-full animate-spin" /> : <Download size={18} />}
                      <span className="text-2xs font-black uppercase tracking-widest">{isGenerating ? 'Wait...' : 'Export PNG'}</span>
                    </button>
                  </div>
                </div>
              </div>

              {/* 3. TIPS (Minimalist) */}
              <div className="pt-6 border-t border-gray-100 dark:border-gray-800">
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                      <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    </div>
                    <div>
                      <p className="text-[11px] font-black text-gray-800 dark:text-white uppercase tracking-wider">Smart Sharing</p>
                      <p className="text-2xs font-bold text-gray-500 dark:text-gray-500 leading-relaxed mt-1">Copy Link for Instagram Stories to enable tapping. Use PNG for Groups.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
